import { useState, useMemo, useEffect } from 'react';
import { Search, GripVertical } from 'lucide-react';
import { useDraggable } from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Skeleton } from '../ui/skeleton';
import { useModalActivities } from '../../hooks/useActivities';
import { TIME_PERIOD_SECTIONS } from '../../utils/timePeriodConfig';
import type { ModalActivity } from '../../types/activity';

interface ActivitySelectionListProps {
  transitions: { id: string; name: string; label: string }[];
  isDragSaving?: boolean;
  ageRangeFilter?: string; // format "fromAge-toAge" e.g. "2-3"
}

export function ActivitySelectionList({
  transitions,
  isDragSaving,
  ageRangeFilter,
}: ActivitySelectionListProps) {
  // Data fetching
  const { data: activitiesData, isLoading } = useModalActivities();

  const activities = useMemo(() => activitiesData ?? [], [activitiesData]);

  // State
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTransitionId, setSelectedTransitionId] = useState<string | ''>('');

  // Load filter from sessionStorage on mount
  useEffect(() => {
    const saved = sessionStorage.getItem('calendar-modal-transition-filter');
    if (saved) setSelectedTransitionId(saved);
  }, []);

  // Save filter to sessionStorage on change
  useEffect(() => {
    if (selectedTransitionId) {
      sessionStorage.setItem('calendar-modal-transition-filter', selectedTransitionId);
    } else {
      sessionStorage.removeItem('calendar-modal-transition-filter');
    }
  }, [selectedTransitionId]);

  // Filtering logic
  const filteredActivities = useMemo(() => {
    let filtered = activities;

    // Text search
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(a => a.title.toLowerCase().includes(term));
    }

    // Time period filter
    if (selectedTransitionId) {
      filtered = filtered.filter(a =>
        a.transitions.some(t => t.id === selectedTransitionId)
      );
    }

    // Age range filter
    if (ageRangeFilter) {
      const [fromStr, toStr] = ageRangeFilter.split('-');
      const fromAge = parseInt(fromStr, 10);
      const toAge = parseInt(toStr, 10);
      filtered = filtered.filter(a =>
        a.ageRanges.some(ar => ar.fromAge === fromAge && ar.toAge === toAge)
      );
    }

    // Sort alphabetically
    filtered = [...filtered].sort((a, b) => a.title.localeCompare(b.title));

    return filtered;
  }, [activities, searchTerm, selectedTransitionId, ageRangeFilter]);

  return (
    <div className="space-y-3">
      <h3 className="text-sm font-semibold text-gray-700">Add Activities</h3>

      {/* Search and Filter row */}
      <div className="flex gap-3">
        {/* Search input */}
        <div className="relative flex-1">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
          <Input
            placeholder="Search activities..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 bg-gray-100"
          />
        </div>

        {/* Time period filter */}
        <select
          value={selectedTransitionId}
          onChange={(e) => setSelectedTransitionId(e.target.value)}
          className="h-10 rounded-md border border-input px-3 py-2 text-sm bg-gray-100 text-gray-400"
        >
          <option value="">All time periods</option>
          {transitions.map(t => (
            <option key={t.id} value={t.id}>{t.label}</option>
          ))}
        </select>
      </div>

      {/* Activity list */}
      <div className="border rounded-md max-h-48 overflow-y-auto divide-y">
        {isLoading ? (
          <div className="p-4 space-y-2">
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-8 w-full" />
          </div>
        ) : filteredActivities.length === 0 ? (
          <div className="px-4 py-6 text-center text-sm text-gray-500">
            No activities found
          </div>
        ) : (
          filteredActivities.map(activity => (
            <DraggableActivityItem
              key={activity.id}
              activity={activity}
              isDragDisabled={isDragSaving}
            />
          ))
        )}
      </div>
    </div>
  );
}

/**
 * Individual activity item that can be dragged from the list into a time period column.
 * Uses `useDraggable` (not `useSortable`) since the list itself doesn't need reordering.
 */
function DraggableActivityItem({
  activity,
  isDragDisabled,
}: {
  activity: ModalActivity;
  isDragDisabled?: boolean;
}) {
  const { attributes, listeners, setNodeRef, isDragging, transform } = useDraggable({
    id: `activity-${activity.id}`,
    disabled: isDragDisabled,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    opacity: isDragging ? 0.3 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex items-center px-3 py-2 hover:bg-gray-50"
    >
      <button
        {...attributes}
        {...listeners}
        className="cursor-grab active:cursor-grabbing touch-none p-1 text-gray-400 hover:text-gray-600 mr-1"
        title="Drag to schedule"
      >
        <GripVertical className="h-3 w-3" />
      </button>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-gray-900 truncate">
          {activity.title}
        </p>
        <div className='flex flex-row items-center gap-2'>
        {/* Time period badges */}
        {activity.transitions.length > 0 && (
          <div className="flex gap-1 mt-0.5">
            {activity.transitions.map(t => {
              const config = TIME_PERIOD_SECTIONS.find(
                s => s.transitionName === t.name
              );
              return (
                <Badge
                  key={t.id}
                  variant="outline"
                  className="text-[10px] px-1.5 py-0"
                  style={{
                    borderColor: config?.headerColor,
                    color: config?.headerColor,
                  }}
                >
                  {t.label}
                </Badge>
              );
            })}
          </div>
        )}
        {/* Age group badges */}
        {activity.ageRanges.length > 0 && (
          <div className="flex gap-1 mt-0.5">
            {activity.ageRanges.map(ar => (
              <Badge
                key={`${ar.fromAge}-${ar.toAge}`}
                variant="outline"
                className="text-[10px] px-1.5 py-0 border-gray-400 text-gray-500"
              >
                {ar.fromAge}-{ar.toAge} yrs
              </Badge>
            ))}
          </div>
        )}
        </div>
      </div>
    </div>
  );
}
